import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Colors from "../../../../Helper/ColorsConstant";

const getHeaderColor = (percent) => {
    const pct = parseFloat(String(percent || "0").replace('%', '')) || 0;
    if (pct >= 100) return "#1a1a1a";
    if (pct < 100 && pct >= 66.66) return "rgb(186, 77, 81)"//"#de0518";//"rgb(229, 82, 83)";  //red
    if (pct < 66.66 && pct >= 33.33) return "rgb(232, 194, 103)"; //yellow
    if (pct < 33.33) return "rgb(154, 181, 126)";//"rgb(133, 150, 102)";//"rgb(154, 181, 126)"; //green
    return "#1a1a1a";
};

const getStackHeaderBorderColor = (headerColor) => {
    const match = headerColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (!match) return "rgba(255,255,255,0.2)";
    const factor = 0.68;
    return `rgb(${Math.round(parseInt(match[1]) * factor)}, ${Math.round(parseInt(match[2]) * factor)}, ${Math.round(parseInt(match[3]) * factor)})`;
};

const areSameCards = (a, b) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        const p = a[i]; const n = b[i];
        if (
            p.id !== n.id || p.title !== n.title || p.description !== n.description ||
            p.owner !== n.owner || p.percent !== n.percent || p.topLeftText !== n.topLeftText ||
            p.imageUrl !== n.imageUrl || p.openDate !== n.openDate || p.promiseDate !== n.promiseDate ||
            p.status !== n.status || p.headerText !== n.headerText ||
            p.completedDate !== n.completedDate || p.targetHours !== n.targetHours ||
            p.actualHours !== n.actualHours || p.description1 !== n.description1
        ) return false;
    }
    return true;
};


export default function WidgetCards({
    cardsData = [],
    minOpenTicketCount,
    autoRevertDurationinSec = 0,
    isDarkTheme = false,
    OpenTicketPlacement,
}) {
    const parentRef = useRef(null);
    const gridRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const cardRefs = useRef([]);
    const autoRevertTimerRef = useRef(null);
    const resizeDebounceRef = useRef(null);
    const lastCalcTimeRef = useRef(0);
    const initialStateRef = useRef(null);
    const isFirstCalculationRef = useRef(true);
    const calcRetryRef = useRef(0);
    const calcHandlerRef = useRef(null);
    const calcRunIdRef = useRef(0);
    const dataVersionRef = useRef(0);
    const isCalculatingRef = useRef(false);
    const appliedMeasureTokenRef = useRef(-1);
    const layoutMeasureTokenRef = useRef(0);
    const prevDisplayCardsRef = useRef([]);
    const prevLayoutDepsRef = useRef({ minOpenTicketCount: null, isOpenTicketPlacementTop: null });
    const colors = Colors;
    const normalizedCardsData = useMemo(() => (Array.isArray(cardsData) ? cardsData : []), [cardsData]);
    const [widgetViewportHeight, setWidgetViewportHeight] = useState(0);
    const headerLabelModesRef = useRef({});
    const headerSpanRefs = useRef({});
    const [headerLabelVersion, setHeaderLabelVersion] = useState(0);
    const prevHeaderDataKeyRef = useRef(null);
    const defaultHeightOfOpenCard = 180;
    const defaultHeightOfSingleCard = 180;
    const defaultHeightOfHeaderPx = 21;
    const defaultHeightOfAfter3HeaderPx = 25;
    const defaultEstimatedCardSpacingPx = 4.3;
    const defaultPercentageOfOrphanCard = 0.33;

    const MIN_MEDIUM_HEADERS = 3;

    const [isToggling, setIsToggling] = useState(null);
    const hasUserInteractedRef = useRef(false);
    const isEntryAnimatingRef = useRef(false);   // true while initial load animation is playing
    const [entryAnimStage, setEntryAnimStage] = useState(0); // 0=idle, 1=stack slide-in, 2=headers open, 3=bodies open
    const entryAnimTimersRef = useRef([]);
    const [scrollEnabled, setScrollEnabled] = useState(false);

    const isOpenTicketPlacementTop = useMemo(
        () => String(OpenTicketPlacement || "bottom").toLowerCase() === "top",
        [OpenTicketPlacement]
    );
    const safeMinOpenCountForRender = useMemo(
        () => (Number.isFinite(Number(minOpenTicketCount)) ? Math.max(1, Math.min(5, Math.floor(Number(minOpenTicketCount)))) : 1),
        [minOpenTicketCount]
    );


    const estimatedTicketsToRender = useMemo(() => {
        const totalTickets = normalizedCardsData.length;
        if (totalTickets === 0) return 0;

        const minOpen = Math.min(safeMinOpenCountForRender, totalTickets);
        const containerPadPx = 8;
        const effectiveHeight = Math.max(0, widgetViewportHeight - (2 * containerPadPx));
        console.log('effectiveHeight', effectiveHeight);
        if (effectiveHeight <= 0) return minOpen;

        const estimatedCardSpacingPx = defaultEstimatedCardSpacingPx;
        const estimateHeaderBlockPx = minOpen > 2 ? defaultHeightOfAfter3HeaderPx : defaultHeightOfHeaderPx;
        console.log('estimateHeaderBlockPx', estimateHeaderBlockPx);
        const estimatedOpenHeaderBlockPx = estimateHeaderBlockPx;
        const estimatedOpenBodyPerTicketPx = minOpen === 1 ? defaultHeightOfSingleCard : defaultHeightOfOpenCard;

        const estimatedHighlyCollapsedHeaderBlockPx = 1;
        const estimatedMediumHeaderBlockPx = estimateHeaderBlockPx;
        const perCollapsedTicketPx = estimatedHighlyCollapsedHeaderBlockPx + estimatedCardSpacingPx + (minOpen > 2 ? 10 : 0);
        const perMediumCollapsedTicketPx = estimatedMediumHeaderBlockPx + estimatedCardSpacingPx;

        console.log("perCollapsedTicketPx", perCollapsedTicketPx);

        // Fit open tickets
        let fittedOpenCount = minOpen;
        while (fittedOpenCount > 0) {
            const openTicketsPx = fittedOpenCount * (estimatedOpenHeaderBlockPx + estimatedOpenBodyPerTicketPx);
            const openSpacingPx = Math.max(0, fittedOpenCount - 1) * estimatedCardSpacingPx;
            if ((openTicketsPx + openSpacingPx) <= effectiveHeight) break;
            fittedOpenCount -= 1;
        }

        const reservedOpenTicketsPx = fittedOpenCount * (estimatedOpenHeaderBlockPx + estimatedOpenBodyPerTicketPx);
        const reservedOpenSpacingPx = Math.max(0, fittedOpenCount - 1) * estimatedCardSpacingPx;
        let remainingHeightPx = Math.max(0, effectiveHeight - (reservedOpenTicketsPx + reservedOpenSpacingPx));

        const baseTickets = fittedOpenCount;
        console.log("remainingHeightPx", remainingHeightPx);

        // Guarantee at least MIN_MEDIUM_HEADERS medium headers in remaining slots
        const nonOpenTickets = totalTickets - fittedOpenCount;
        const guaranteedMediumCount = Math.min(nonOpenTickets, MIN_MEDIUM_HEADERS);
        const guaranteedMediumPx = guaranteedMediumCount * perMediumCollapsedTicketPx;

        // Budget remaining after reserving space for the guaranteed medium headers
        const remainingAfterGuaranteedMedium = Math.max(0, remainingHeightPx - guaranteedMediumPx);

        // Any leftover height → extra medium + collapsed tickets
        const mixWeightMedium = defaultPercentageOfOrphanCard;
        const mediumHeightBudget = remainingAfterGuaranteedMedium * mixWeightMedium;
        const collapsedHeightBudget = Math.max(0, remainingAfterGuaranteedMedium - mediumHeightBudget);
        const extraMediumCollapsedTickets = Math.max(0, Math.floor(mediumHeightBudget / perMediumCollapsedTicketPx));
        const additionalCollapsedTickets = Math.max(0, Math.floor(collapsedHeightBudget / perCollapsedTicketPx));

        console.log("additionalCollapsedTickets", additionalCollapsedTickets);
        console.log("guaranteedMediumCount", guaranteedMediumCount, "extraMediumCollapsedTickets", extraMediumCollapsedTickets);

        return Math.max(
            baseTickets,
            Math.min(
                totalTickets,
                baseTickets + guaranteedMediumCount + extraMediumCollapsedTickets + additionalCollapsedTickets
            )
        );
    }, [normalizedCardsData.length, safeMinOpenCountForRender, widgetViewportHeight]);

    console.log("Estimated tickets to render based on viewport:", estimatedTicketsToRender, "for viewport height:", widgetViewportHeight);

    const [maxTicketsToRender, setMaxTicketsToRender] = useState(estimatedTicketsToRender);

    useEffect(() => {
        setMaxTicketsToRender((prev) => {
            const boundedEstimate = Math.max(
                safeMinOpenCountForRender,
                Math.min(normalizedCardsData.length, estimatedTicketsToRender)
            );
            console.log("Adjusting maxTicketsToRender from", prev, "to", boundedEstimate, "based on new estimate:", estimatedTicketsToRender);
            if (boundedEstimate === prev) return prev;
            console.log("Updated maxTicketsToRender to:", boundedEstimate);
            return boundedEstimate;
        });
    }, [estimatedTicketsToRender, normalizedCardsData.length, safeMinOpenCountForRender]);

    const displayCardsData = useMemo(
        () => {
            console.log("Rendering with maxTicketsToRender:", maxTicketsToRender, "out of", normalizedCardsData.length, normalizedCardsData.slice(-maxTicketsToRender));
            return normalizedCardsData.slice(-maxTicketsToRender);
        },
        [normalizedCardsData, maxTicketsToRender]
    );

    const themeStyles = useMemo(() => ({
        cardBackground: isDarkTheme ? "rgb(52, 52, 52)" : "#ffffff",
        cardBorder: isDarkTheme ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.12)",
        cardBoxShadow: isDarkTheme ? "0 2px 8px rgba(0,0,0,0.60)" : "none",
        bodyTextColor: isDarkTheme ? "#d0d0d0" : "inherit",
        mutedTextColor: isDarkTheme ? "#a8a8a8" : "#6c757d",
    }), [isDarkTheme]);

    const [cardStates, setCardStates] = useState(
        displayCardsData.map((_, index) => ({
            isExpanded: index === 0,
            isHalfOpen: false,
            fullHeight: 0,
            visibleBodyHeight: 0,
            ExtraMargin: 0,
            renderMode: index === 0 ? "open" : "collapsed",
            priorCompressedMode: "collapsed",
            lastCollapsedMode: false
        }))
    );
    const [isReady, setIsReady] = useState(false);
    const [disableBodyTransition, setDisableBodyTransition] = useState(true);
    const [disableHeaderTransition, setDisableHeaderTransition] = useState(true);
    const isRevertAnimatingRef = useRef(false);
    const revertAnimationTimerRef = useRef(null);
    const hasInitiallyLoadedRef = useRef(false);
    const isMeasuringRef = useRef(false);
    const [imageErrors, setImageErrors] = useState({});
    const [compactStatusStyle, setCompactStatusStyle] = useState({
        headerPadPx: 2,
        headerMinHeightPx: 10,
        statusPadPx: 2,
        headerFontRem: 0.75,
        statusLabelRem: 0.8,
        statusValueRem: 1,
        bodyPaddingPx: 8,
        avatarSizePx: 50,
        avatarFontPx: 18,
        ownerFontRem: 0.85,
        topLeftFontRem: 1.0,
        titleFontRem: 1.5,
        descFontRem: 0.9,
        metaFontRem: 0.75,
        titleLines: 1,
        descLines: 2,
        containerPadPx: 3,
        avatarWrapWidthPx: 80,
        showHeaderText: true,
    });

    useLayoutEffect(() => {
        if (!parentRef.current) return;

        const updateViewportHeight = () => {
            const rawHeight = scrollContainerRef.current?.clientHeight || parentRef.current?.clientHeight || 0;
            const quantizedHeight = Math.max(0, Math.round(rawHeight / 8) * 8);
            setWidgetViewportHeight((prev) => (prev === quantizedHeight ? prev : quantizedHeight));
        };

        updateViewportHeight();

        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(() => updateViewportHeight());
            observer.observe(parentRef.current);
            return () => observer.disconnect();
        }

        window.addEventListener("resize", updateViewportHeight);
        return () => window.removeEventListener("resize", updateViewportHeight);
    }, []);

    useLayoutEffect(() => {
        const layoutDepsChanged =
            prevLayoutDepsRef.current.minOpenTicketCount !== minOpenTicketCount ||
            prevLayoutDepsRef.current.isOpenTicketPlacementTop !== isOpenTicketPlacementTop;
        const prevData = prevDisplayCardsRef.current;
        prevDisplayCardsRef.current = displayCardsData;
        prevLayoutDepsRef.current = { minOpenTicketCount, isOpenTicketPlacementTop };

        if (hasInitiallyLoadedRef.current && !layoutDepsChanged && areSameCards(prevData, displayCardsData)) {
            return;
        }

        dataVersionRef.current += 1;
        setIsToggling(null);
        initialStateRef.current = null;
        isFirstCalculationRef.current = true;
        calcRetryRef.current = 0;
        calcRunIdRef.current += 1;
        cardRefs.current = new Array(displayCardsData.length);
        hasUserInteractedRef.current = false;
        prevExpandedRef.current = new Set();
        setScrollEnabled(false);
        entryAnimTimersRef.current.forEach(clearTimeout);
        entryAnimTimersRef.current = [];
        setEntryAnimStage(0);

        if (autoRevertTimerRef.current) {
            clearTimeout(autoRevertTimerRef.current);
            autoRevertTimerRef.current = null;
        }

        setImageErrors({});

        if (displayCardsData.length === 0) {
            setCardStates([]);
            setIsReady(true);
            setDisableBodyTransition(false);
            hasInitiallyLoadedRef.current = true;
            return;
        }

        isCalculatingRef.current = true;
        setIsReady(false);
        setDisableBodyTransition(true);
        setDisableHeaderTransition(true);
        setCardStates(
            displayCardsData.map((_, index) => ({
                isExpanded: false,
                isHalfOpen: false,
                fullHeight: 0,
                visibleBodyHeight: 0,
                ExtraMargin: 0,
                renderMode: "collapsed",
                priorCompressedMode: "collapsed",
            }))
        );

        const scheduleCalc = (delayMs = 0) => {
            const run = () => {
                const doCalc = () => requestAnimationFrame(() => requestAnimationFrame(() => calcHandlerRef.current?.()));
                if (document.fonts && document.fonts.ready) {
                    document.fonts.ready.then(doCalc).catch(doCalc);
                } else {
                    doCalc();
                }
            };
            if (delayMs > 0) return setTimeout(run, delayMs);
            run();
            return null;
        };

        const timer = scheduleCalc(0);

        return () => {
            if (timer) clearTimeout(timer);
            if (autoRevertTimerRef.current) {
                clearTimeout(autoRevertTimerRef.current);
                autoRevertTimerRef.current = null;
            }
            if (revertAnimationTimerRef.current) {
                clearTimeout(revertAnimationTimerRef.current);
                revertAnimationTimerRef.current = null;
            }
            isRevertAnimatingRef.current = false;
            isEntryAnimatingRef.current = false;
            entryAnimTimersRef.current.forEach(clearTimeout);
            entryAnimTimersRef.current = [];
        };
    }, [displayCardsData, minOpenTicketCount, isOpenTicketPlacementTop]);

    useEffect(() => {
        const triggerCalc = () => {
            const run = () => requestAnimationFrame(() => requestAnimationFrame(() => calcHandlerRef.current?.()));
            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(run).catch(run);
            } else {
                run();
            }
        };

        const timer = setTimeout(() => {
            if (!hasInitiallyLoadedRef.current) {
                triggerCalc();
            }
        }, 300);

        const images = parentRef.current
            ? parentRef.current.querySelectorAll(".card img")
            : [];
        const handleImageLoad = () => triggerCalc();

        images.forEach((img) => {
            if (!img.complete) {
                img.addEventListener("load", handleImageLoad);
                img.addEventListener("error", handleImageLoad);
            }
        });

        return () => {
            clearTimeout(timer);
            images.forEach((img) => {
                img.removeEventListener("load", handleImageLoad);
                img.removeEventListener("error", handleImageLoad);
            });
        };
    }, [displayCardsData]);

    useEffect(() => {
        const handleResize = () => {
            if (resizeDebounceRef.current) {
                clearTimeout(resizeDebounceRef.current);
            }
            resizeDebounceRef.current = setTimeout(() => {
                calcHandlerRef.current?.();
                resizeDebounceRef.current = null;
            }, 300);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            if (resizeDebounceRef.current) {
                clearTimeout(resizeDebounceRef.current);
            }
            window.removeEventListener("resize", handleResize);
            if (autoRevertTimerRef.current) {
                clearTimeout(autoRevertTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        calcHandlerRef.current = calculateAndAdjustHeight;
    });

    useLayoutEffect(() => {
        if (!isReady || displayCardsData.length === 0) return;

        const token = layoutMeasureTokenRef.current;
        if (token === appliedMeasureTokenRef.current) return;

        let settleTimer = null;
        let measureRetryCount = 0;
        let raf1 = 0;

        const measureAndApply = () => {
            const gridEl = gridRef.current;
            const viewportEl = scrollContainerRef.current || parentRef.current;
            if (!gridEl || !viewportEl) {
                appliedMeasureTokenRef.current = token;
                return;
            }

            if (isCalculatingRef.current) {
                if (measureRetryCount < 30) {
                    measureRetryCount += 1;
                    settleTimer = setTimeout(measureAndApply, 50);
                }
                return;
            }

            if (isRevertAnimatingRef.current) return;

            const domCardElements = Array.from(gridEl.querySelectorAll(":scope > .card"));
            const cardElements = (domCardElements.length > 0
                ? domCardElements
                : cardRefs.current.filter(Boolean)
            ).slice(0, displayCardsData.length);

            if (cardElements.length === 0) {
                appliedMeasureTokenRef.current = token;
                return;
            }

            if (cardElements.length < displayCardsData.length && measureRetryCount < 30) {
                measureRetryCount += 1;
                settleTimer = setTimeout(measureAndApply, 50);
                return;
            }

            if (cardElements.length < displayCardsData.length) {
                console.log("Skipping trailing height measurement until all cards mount:", cardElements.length, "/", displayCardsData.length);
                return;
            }

            const viewportStyles = window.getComputedStyle(viewportEl);
            const viewportPaddingTop = parseFloat(viewportStyles.paddingTop || "0") || 0;
            const viewportPaddingBottom = parseFloat(viewportStyles.paddingBottom || "0") || 0;
            const viewportInnerHeight = Math.max(0, viewportEl.clientHeight - viewportPaddingTop - viewportPaddingBottom);

            const gridStyles = window.getComputedStyle(gridEl);
            const gridMarginTop = parseFloat(gridStyles.marginTop || "0") || 0;
            const gridMarginBottom = parseFloat(gridStyles.marginBottom || "0") || 0;
            const gridTotalMargin = gridMarginTop + gridMarginBottom;
            const contentHeightFromChildren = Array.from(gridEl.children).reduce((sum, child) => {
                const childStyles = window.getComputedStyle(child);
                const marginTop = parseFloat(childStyles.marginTop || "0") || 0;
                const marginBottom = parseFloat(childStyles.marginBottom || "0") || 0;
                return sum + child.getBoundingClientRect().height + marginTop + marginBottom;
            }, 0);
            // Use per-child getBoundingClientRect().height (not gridEl.scrollHeight).
            // scrollHeight on overflow:visible elements IS inflated by transform:translateY
            // on children (the scroll area includes the translated visual position).
            // getBoundingClientRect().height on each card reports its layout height
            // independently of any translateY, so it's always accurate.
            const contentHeight = Math.ceil(contentHeightFromChildren) + gridTotalMargin;

            console.log('viewportInnerHeight', viewportInnerHeight, 'contentHeight', contentHeight, 'contentHeightFromChildren', contentHeightFromChildren);

            appliedMeasureTokenRef.current = token;

            // After user interaction: open cards sit at their natural scrollHeight (corrected
            // by the newly-opened effect) and overflow scroll handles the rest — skip all
            // adjustments to prevent the grow/shrink feedback loop.
            if (hasUserInteractedRef.current) return;
            if (isEntryAnimatingRef.current) return;

            // Initial load only: correct open card heights so content fits within the
            // viewport with exactly the 8px grid bottom margin.
            // trailingSpace > 0 → content is short, grow open cards.
            // trailingSpace < 0 → content overflows (e.g. collapsed card borders with
            //   box-sizing:content-box add ~1px each beyond what the baseline estimates),
            //   shrink open cards to compensate so the bottom margin is preserved.
            let trailingSpace = viewportInnerHeight - contentHeight;
            if (trailingSpace === 0) return;

            setCardStates((prevStates) => {
                const openIndexes = [];
                prevStates.forEach((state, idx) => {
                    if (state.isExpanded) openIndexes.push(idx);
                });
                if (openIndexes.length === 0) return prevStates;

                // floor keeps shrink corrections safe (never over-shrinks by a fraction).
                const adjustPerCard = Math.floor(trailingSpace / openIndexes.length);


                console.log("Adjusting open cards by:", trailingSpace, "px total,", adjustPerCard, "px per card");
                if (adjustPerCard === 0) return prevStates;

                const openIndexSet = new Set(openIndexes);
                const nextStates = prevStates.map((state, idx) => {
                    if (!openIndexSet.has(idx)) return state;
                    return {
                        ...state,
                        visibleBodyHeight: Math.max(1, Number(state.visibleBodyHeight || 0) + adjustPerCard),
                    };
                });

                if (initialStateRef.current &&
                    initialStateRef.current.dataVersion === dataVersionRef.current &&
                    initialStateRef.current.states.length === nextStates.length) {
                    initialStateRef.current = {
                        ...initialStateRef.current,
                        states: JSON.parse(JSON.stringify(nextStates)),
                    };
                }

                return nextStates;
            });
        };

        // measureAndApply is the final correction pass after the initial layout.
        // applyOpenTicketLayout pre-fills as much as possible, but box-sizing:content-box
        // padding/border overheads on collapsed and open cards can still leave a small
        // gap or overflow. measureAndApply reads actual DOM heights and adjusts the open
        // card body by exactly that delta — growing if there's trailing space, shrinking
        // if there's overflow — so the 8px grid bottom margin is always pixel-perfect.
        raf1 = requestAnimationFrame(measureAndApply);

        return () => {
            if (raf1) cancelAnimationFrame(raf1);
            if (settleTimer) clearTimeout(settleTimer);
        };
    }, [cardStates, isReady, displayCardsData.length, disableBodyTransition]);

    // One-shot height correction: when a card transitions to open, measure its real scrollHeight
    // and grow visibleBodyHeight if the pre-calculated estimate is too small. Fires only for
    // cards that just became open (wasOpen false → true) to avoid fighting measureAndApply.
    // Skipped on initial load (before any user toggle) so the viewport-fitted initial layout
    // is preserved and no spurious scrollbar appears.
    const prevExpandedRef = useRef(new Set());
    useLayoutEffect(() => {
        if (!isReady || !hasUserInteractedRef.current) return;
        const currentExpanded = new Set(
            cardStates.flatMap((s, i) => s.isExpanded ? [i] : [])
        );
        const newlyOpened = [...currentExpanded].filter(i => !prevExpandedRef.current.has(i));
        prevExpandedRef.current = currentExpanded;
        if (newlyOpened.length === 0) return;
        const fixes = {};
        newlyOpened.forEach(i => {
            const body = cardRefs.current[i]?.querySelector(".card-body");
            if (!body) return;
            const natural = body.scrollHeight;
            if (natural > Number(cardStates[i]?.visibleBodyHeight || 0) + 2) fixes[i] = natural;
        });
        if (Object.keys(fixes).length === 0) return;
        layoutMeasureTokenRef.current += 1;
        setCardStates(prev => prev.map((s, i) =>
            fixes[i] !== undefined && s.isExpanded
                ? { ...s, visibleBodyHeight: fixes[i], fullHeight: fixes[i] }
                : s
        ));
    }, [cardStates, isReady]);

    useLayoutEffect(() => {
        const dataKey = displayCardsData.map(c => c.id).join(',');
        if (prevHeaderDataKeyRef.current !== dataKey) {
            headerLabelModesRef.current = {};
            prevHeaderDataKeyRef.current = dataKey;
        }
        let changed = false;
        displayCardsData.forEach((card) => {
            const span = headerSpanRefs.current[card.id];
            if (!span) return;
            if (span.scrollWidth > span.clientWidth && (headerLabelModesRef.current[card.id] || 'full') === 'full') {
                headerLabelModesRef.current[card.id] = 'short';
                changed = true;
            }
        });
        if (changed) setHeaderLabelVersion(v => v + 1);
    }, [displayCardsData, headerLabelVersion]);

    const calculateAndAdjustHeight = () => {
        if (displayCardsData.length === 0) {
            setIsReady(true);
            setDisableBodyTransition(false);
            return;
        }

        const now = Date.now();
        const isInitialLoadStabilizing = !hasInitiallyLoadedRef.current;
        if (!isFirstCalculationRef.current && !isInitialLoadStabilizing && now - lastCalcTimeRef.current < 400) return;

        if (!parentRef.current || cardRefs.current.length === 0) {
            if (calcRetryRef.current < 5) {
                calcRetryRef.current += 1;
                setTimeout(calculateAndAdjustHeight, 100);
            }
            return;
        }

        isFirstCalculationRef.current = false;
        lastCalcTimeRef.current = now;

        const parentHeight = parentRef.current.clientHeight;
        const cardElements = cardRefs.current.slice(0, displayCardsData.length);

        const containerHeight = Math.max(
            0,
            widgetViewportHeight || scrollContainerRef.current?.clientHeight || parentHeight
        );

        const primaryOpenBodyPx = minOpenTicketCount === 1 ? defaultHeightOfSingleCard : defaultHeightOfOpenCard;

        if (cardElements.length === 0 || cardElements.some((el) => !el)) {
            if (calcRetryRef.current < 5) {
                calcRetryRef.current += 1;
                setTimeout(calculateAndAdjustHeight, 100);
            }
            return;
        }

        isCalculatingRef.current = true;
        isMeasuringRef.current = true;
        setDisableBodyTransition(true);

        const currentCalcRunId = ++calcRunIdRef.current;
        requestAnimationFrame(() => {
            const restoreMeasuredBodies = () => { };

            if (currentCalcRunId !== calcRunIdRef.current) {
                restoreMeasuredBodies();
                isMeasuringRef.current = false;
                setDisableBodyTransition(false);
                return;
            }

            const cardSpacing = cardElements.map((card) => {
                const styles = window.getComputedStyle(card);
                const marginBottom = parseFloat(styles.marginBottom || "0");
                return Number.isFinite(marginBottom) ? marginBottom : 0;
            });

            const cardHeights = cardElements.map((card) => {
                const body = card.querySelector(".card-body");
                const measuredBodyHeight = body ? body.scrollHeight : 0;
                return Math.max(primaryOpenBodyPx, Math.floor(measuredBodyHeight || 0));
            });

            restoreMeasuredBodies();
            isMeasuringRef.current = false;
            // Keep disableBodyTransition = true so the initial layout render has no
            // height animation. This ensures measureAndApply reads settled DOM heights
            // (not mid-animation values) when it fires 150ms later.
            // disableBodyTransition is set to false in toggleCard on first user interaction.

            const containerPadPx = 8;
            const availableHeight = containerHeight - (containerPadPx * 2);
            const perCardHeight = availableHeight / Math.max(cardElements.length, 1);
            const compressionRatio = Math.max(0, Math.min(1, (90 - perCardHeight) / 60));
            const roomyRatio = 1 - compressionRatio;

            const headerPadPx = 2;
            const headerMinHeightPx = Math.max(2, Math.round(3 + (11 * roomyRatio)));
            const statusPadPx = Math.max(0, Math.round(4 * roomyRatio));
            const headerFontRem = Number((0.56 + (0.26 * roomyRatio)).toFixed(2));
            const statusLabelRem = 0.8;
            const statusValueRem = 1;
            const bodyPaddingPx = 8;
            const avatarSizePx = 50;
            const avatarFontPx = 18;
            const ownerFontRem = 0.85;
            const topLeftFontRem = 1.0;
            const titleFontRem = 1.5;
            const descFontRem = 0.9;
            const metaFontRem = 0.75;
            const titleLines = 2;
            const descLines = 2;
            const avatarWrapWidthPx = 80;
            const showHeaderText = true;

            const safeMinOpenCount = Number.isFinite(Number(minOpenTicketCount))
                ? Math.max(1, Math.min(5, Math.floor(Number(minOpenTicketCount))))
                : 1;

            const newCardStates = displayCardsData.map((_, index) => ({
                isExpanded: false,
                isHalfOpen: false,
                fullHeight: cardHeights[index] || 0,
                visibleBodyHeight: 0,
                ExtraMargin: 0,
                renderMode: "collapsed",
                priorCompressedMode: "collapsed",
            }));

            const totalSpacing = cardSpacing.slice(0, -1).reduce((sum, spacing) => sum + spacing, 0);

            setCompactStatusStyle((prev) => {
                if (
                    prev.headerPadPx === headerPadPx &&
                    prev.headerMinHeightPx === headerMinHeightPx &&
                    prev.statusPadPx === statusPadPx &&
                    prev.headerFontRem === headerFontRem &&
                    prev.statusLabelRem === statusLabelRem &&
                    prev.statusValueRem === statusValueRem &&
                    prev.bodyPaddingPx === bodyPaddingPx &&
                    prev.avatarSizePx === avatarSizePx &&
                    prev.avatarFontPx === avatarFontPx &&
                    prev.ownerFontRem === ownerFontRem &&
                    prev.topLeftFontRem === topLeftFontRem &&
                    prev.titleFontRem === titleFontRem &&
                    prev.descFontRem === descFontRem &&
                    prev.metaFontRem === metaFontRem &&
                    prev.titleLines === titleLines &&
                    prev.descLines === descLines &&
                    prev.containerPadPx === containerPadPx &&
                    prev.avatarWrapWidthPx === avatarWrapWidthPx &&
                    prev.showHeaderText === showHeaderText
                ) {
                    return prev;
                }
                return {
                    headerPadPx,
                    headerMinHeightPx,
                    statusPadPx,
                    headerFontRem,
                    statusLabelRem,
                    statusValueRem,
                    bodyPaddingPx,
                    avatarSizePx,
                    avatarFontPx,
                    ownerFontRem,
                    topLeftFontRem,
                    titleFontRem,
                    descFontRem,
                    metaFontRem,
                    titleLines,
                    descLines,
                    containerPadPx,
                    avatarWrapWidthPx,
                    showHeaderText,
                };
            });

            const normalHeaderPadPxForCalc = 0;
            const normalHeaderMinHeightPxForCalc = minOpenTicketCount >= 3 ? defaultHeightOfAfter3HeaderPx : defaultHeightOfHeaderPx;
            const mediumHeaderMinHeightPxForCalc = normalHeaderMinHeightPxForCalc;
            const collapsedHeaderPadPxForCalc = 0;
            // Collapsed header has border:1px solid with box-sizing:content-box
            // → visual height = 3(minHeight content) + 2(border top+bottom) = 5px
            const collapsedHeaderMinHeightPxForCalc = 5;

            const normalHeaderBlockPxForCalc = normalHeaderMinHeightPxForCalc + (normalHeaderPadPxForCalc * 2);
            const mediumHeaderBlockPxForCalc = mediumHeaderMinHeightPxForCalc + defaultEstimatedCardSpacingPx;
            const collapsedHeaderBlockPxForCalc = collapsedHeaderMinHeightPxForCalc + (collapsedHeaderPadPxForCalc * 2);

            const compressedHeaderHeight = collapsedHeaderBlockPxForCalc;
            const collapsedBaselineHeight = cardElements.length * compressedHeaderHeight + totalSpacing;
            console.log("collapsedBaselineHeight", collapsedBaselineHeight, "px");
            let availableBodyHeight = Math.max(0, availableHeight - collapsedBaselineHeight);

            // ─── Reserve space for guaranteed medium (orphan) header upgrades ────
            // Each medium card costs (mediumHeaderBlockPx - collapsedHeaderBlockPx)
            // more than the collapsed baseline already budgets.  If we don't subtract
            // this upfront, the open-card fit check is too optimistic and the layout
            // overflows once applyCompressedModes promotes N cards to medium.
            //
            // Priority: open cards first, then orphan headers, then stack headers.
            // We reserve the medium upgrade cost here so open cards are only counted
            // as fitting if they leave enough room for the orphan headers too.
            const mediumUpgradePerCardPx = Math.max(0, mediumHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc);
            const potentialMediumCount = Math.min(Math.max(0, cardElements.length - 1), MIN_MEDIUM_HEADERS);
            const reservedMediumUpgradePx = potentialMediumCount * mediumUpgradePerCardPx;
            // Budget for deciding how many open cards fit (reduced by medium reserve)
            const availableBodyForOpenFit = Math.max(0, availableBodyHeight - reservedMediumUpgradePx);

            const fullOpenFallbackPx = defaultHeightOfOpenCard;

            const buildOpenIndexes = (count) => (count > 0
                ? (isOpenTicketPlacementTop
                    ? Array.from({ length: count }, (_, idx) => idx)
                    : Array.from({ length: count }, (_, idx) => cardElements.length - count + idx))
                : []);

            const allOpenHeight = cardElements.reduce((sum, _, idx) => {
                const fullHeight = Math.max(1, Math.floor(cardHeights[idx] || fullOpenFallbackPx));
                return sum + fullHeight + normalHeaderBlockPxForCalc;
            }, 0) + totalSpacing;
            const allCardsFitOpen = allOpenHeight <= availableHeight;

            const targetOpenCount = Math.min(safeMinOpenCount, cardElements.length);
            let fittedOpenCount = allCardsFitOpen ? cardElements.length : targetOpenCount;
            if (!allCardsFitOpen) {
                while (fittedOpenCount > 0) {
                    const candidateIndexes = buildOpenIndexes(fittedOpenCount);
                    const candidateOpenHeights = candidateIndexes.reduce((sum, idx) => {
                        const fullHeight = Math.max(1, Math.floor(cardHeights[idx] || fullOpenFallbackPx));
                        return sum + fullHeight;
                    }, 0);
                    const candidateHeaderCost = candidateIndexes.length * normalHeaderBlockPxForCalc;
                    // Use the medium-reserve-adjusted budget so orphan headers are
                    // guaranteed to fit alongside however many cards are open.
                    if ((candidateOpenHeights + candidateHeaderCost) <= availableBodyForOpenFit) break;
                    fittedOpenCount -= 1;
                }
            }

            const openIndexes = buildOpenIndexes(fittedOpenCount);
            const openIndexSet = new Set(openIndexes);
            const openCardsMeta = openIndexes.map((idx) => ({
                idx,
                fullHeight: Math.max(1, Math.floor(cardHeights[idx] || fullOpenFallbackPx)),
            }));

            const applyOpenTicketLayout = (openBodyBudgetPx) => {
                const safeBudget = Math.max(0, Math.floor(openBodyBudgetPx));
                const openCount = openCardsMeta.length;
                if (openCount === 0 || safeBudget <= 0) return 0;

                // The card body uses box-sizing:content-box with padding:8px on each side.
                // Rendered body height = visibleBodyHeight (CSS height) + 16px (padding).
                // The open card div also has border:1px solid → +2px visual height.
                // Subtract both so the card doesn't visually overflow its budget.
                const bodyPadOverhead = 2 * bodyPaddingPx + 2; // 16px body padding + 2px card border (content-box)
                const contentBudget = Math.max(0, safeBudget - bodyPadOverhead * openCount);

                const allocatedBodies = openCardsMeta.map((item) => item.fullHeight);
                const requiredBudget = allocatedBodies.reduce((sum, value) => sum + value, 0);
                if (contentBudget < requiredBudget) return 0;

                const leftoverBudget = contentBudget - requiredBudget;
                const extraPerCard = openCount > 0 ? Math.floor(leftoverBudget / openCount) : 0;

                console.log("Extra space to open cards:", leftoverBudget, "px total,", extraPerCard, "px per card");

                openCardsMeta.forEach((item, orderIdx) => {
                    const visibleBodyHeight = Math.max(1, Math.floor(allocatedBodies[orderIdx])) + extraPerCard;
                    newCardStates[item.idx].isExpanded = true;
                    newCardStates[item.idx].isHalfOpen = false;
                    newCardStates[item.idx].visibleBodyHeight = visibleBodyHeight;
                    newCardStates[item.idx].renderMode = "open";
                });

                const consumedContent = requiredBudget + extraPerCard * openCount;
                // Return the visual budget consumed (content + padding), so the remaining
                // availableBodyHeight is correctly reduced for applyCompressedModes.
                return Math.min(safeBudget, consumedContent + bodyPadOverhead * openCount);
            };

            if (openIndexes.length > 0) {
                // Feed the open card layout the reduced (medium-reserve-adjusted) budget
                // so it doesn't consume space that belongs to the orphan headers.
                const openHeaderBudgetPx = openIndexes.length * normalHeaderBlockPxForCalc;
                const openBodyBudgetPx = Math.max(0, availableBodyForOpenFit - openHeaderBudgetPx);
                const consumedOpenBodyPx = applyOpenTicketLayout(openBodyBudgetPx);
                const consumedOpenBudgetPx = openHeaderBudgetPx + consumedOpenBodyPx;
                // Update the full availableBodyHeight (not the adjusted one) so the
                // reserved medium upgrade budget remains available for applyCompressedModes.
                availableBodyHeight = Math.max(0, availableBodyHeight - consumedOpenBudgetPx);
            }

            const remainingIndexes = [];
            for (let idx = 0; idx < cardElements.length; idx++) {
                if (!openIndexSet.has(idx)) {
                    remainingIndexes.push(idx);
                }
            }

            // ─────────────────────────────────────────────────────────────────
            // applyCompressedModes  (CHANGED)
            //
            // Layout rule for non-open cards:
            //   1. Always show at least MIN_MEDIUM_HEADERS as "medium" (visible
            //      header text), capped by the actual number of non-open cards.
            //   2. If budget allows more medium headers beyond the guarantee,
            //      add them using the existing weight-based budget.
            //   3. All remaining cards become "collapsed" (stack / highly-
            //      collapsed headers — tiny colour stripe only).
            //
            // This ensures: open tickets → medium headers (≥3) → collapsed stack
            // ─────────────────────────────────────────────────────────────────
            const applyCompressedModes = (indexes, remainingBodyHeightPx) => {
                console.log("remainingBodyHeightPx", remainingBodyHeightPx);

                const totalRemainingSpace = remainingBodyHeightPx + collapsedBaselineHeight;
                const nonOpenCount = indexes.length;

                if (nonOpenCount === 0) return 0;

                // Cost of one medium header slot above the collapsed baseline
                const mediumUpgradePx = Math.max(1, mediumHeaderBlockPxForCalc);

                // Maximum medium cards the UPGRADE BUDGET (remainingBodyHeightPx) can afford.
                // Previously used totalRemainingSpace * 0.33, but that includes collapsedBaselineHeight
                // which grows with more collapsed cards, inflating the count beyond the budget
                // and causing overflow when collapsedHeaderBlockPxForCalc is corrected to 5.
                // Using the actual upgrade budget directly keeps mediumCount ≤ what fits.
                const maxMediumByBudget = Math.min(
                    nonOpenCount,
                    Math.floor((totalRemainingSpace * defaultPercentageOfOrphanCard) / mediumUpgradePx)
                );

                // Guarantee MIN_MEDIUM_HEADERS orphan headers — but only when the
                // remaining space can actually afford them.  If it cannot (e.g. the
                // open cards already consumed most of the viewport), fall back to the
                // budget-based count so the layout doesn't overflow.
                const canAffordGuarantee = remainingBodyHeightPx >= (MIN_MEDIUM_HEADERS * mediumUpgradePx);
                const guaranteedMedium = canAffordGuarantee ? Math.min(nonOpenCount, MIN_MEDIUM_HEADERS) : 0;

                // Final count: honour the guarantee when affordable; also take any extra budget
                const mediumCount = Math.max(guaranteedMedium, maxMediumByBudget);

                console.log("mediumCount (with guarantee)", mediumCount, "of", nonOpenCount);

                // Allocation order: closest non-open cards to the open ones first.
                // "top" placement → allocate from first non-open upward
                // "bottom" placement → allocate from last non-open downward
                const allocationOrder = isOpenTicketPlacementTop
                    ? indexes
                    : [...indexes].reverse();

                //let findFirstLastMedium = false;
                allocationOrder.forEach((idx, orderIdx) => {
                    const shouldBeMedium = orderIdx < mediumCount;
                    newCardStates[idx].lastCollapsedMode = false;

                    // Mark the first card that transitions from medium → collapsed
                    // so the spacing logic can insert a small gap there.
                    // if (!shouldBeMedium && !findFirstLastMedium) {
                    //     findFirstLastMedium = true;
                    //     console.log("First collapsed-stack card at index", idx);
                    //     newCardStates[idx].lastCollapsedMode = true;
                    // }

                    newCardStates[idx].isExpanded = false;
                    newCardStates[idx].isHalfOpen = false;
                    newCardStates[idx].visibleBodyHeight = 0;
                    // "medium"    → normal-sized header with visible label text
                    // "collapsed" → tiny colour stripe (stack / highly-collapsed)
                    newCardStates[idx].renderMode = shouldBeMedium ? "medium" : "collapsed";
                    newCardStates[idx].priorCompressedMode = shouldBeMedium ? "medium" : "collapsed";
                });

                const consumedBudgetPx = mediumCount * mediumUpgradePx;
                return Math.max(0, remainingBodyHeightPx - consumedBudgetPx);


            };

            applyCompressedModes(remainingIndexes, availableBodyHeight);

            // Mark calculation complete BEFORE setting states
            isCalculatingRef.current = false;

            if (currentCalcRunId !== calcRunIdRef.current) return;

            console.log("Calculated card states:", newCardStates);

            layoutMeasureTokenRef.current += 1;

            if (!initialStateRef.current) {
                initialStateRef.current = {
                    states: JSON.parse(JSON.stringify(newCardStates)),
                    dataVersion: dataVersionRef.current
                };
            }

            // ── Entry animation: 3-stage sequence ──────────────────────────────
            // Stage 1: cards appear suddenly (no fade/slide), one by one top→bottom
            // Stage 2: headers expand one by one top→bottom
            // Stage 3: bodies expand one by one top→bottom
            const stage1States = newCardStates.map(s => ({
                ...s,
                isExpanded: false,
                visibleBodyHeight: 0,
                renderMode: "collapsed",
                lastCollapsedMode: false,
            }));

            hasInitiallyLoadedRef.current = true;
            setCardStates(stage1States);
            calcRetryRef.current = 0;

            requestAnimationFrame(() => {
                if (currentCalcRunId !== calcRunIdRef.current) return;
                isEntryAnimatingRef.current = true;

                const N = displayCardsData.length;
                const STAGGER_CARD = 60;
                const STAGGER_HEADER = 250;  // matches entry header transition (220ms) + buffer
                const STAGGER_BODY = 950;    // matches body transition (900ms) + buffer
                const HEADER_TRANSITION_MS = 220; // entry transition duration (must match CSS above)
                const BODY_TRANSITION_MS = 900;   // same as interactive body transition

                // Schedule a timer, guard stale runs, and track ID for cleanup
                const addTimer = (fn, delay) => {
                    const id = setTimeout(() => {
                        if (currentCalcRunId !== calcRunIdRef.current) return;
                        fn();
                    }, delay);
                    entryAnimTimersRef.current.push(id);
                };

                setEntryAnimStage(1);
                setIsReady(true);

                // Stage 3: expand bodies one by one, then finish.
                // Only wait STAGGER_BODY after an expanded card — skip non-expanded instantly
                // so collapsed cards don't burn stagger time doing nothing.
                const startStage3 = () => {
                    setDisableBodyTransition(false);
                    setEntryAnimStage(3);
                    const revealBody = (i) => {
                        if (i >= N) {
                            addTimer(() => {
                                setEntryAnimStage(0);
                                isEntryAnimatingRef.current = false;
                                layoutMeasureTokenRef.current += 1;
                                setCardStates(prev => prev.map(s => ({ ...s })));
                            }, BODY_TRANSITION_MS + 200);
                            return;
                        }
                        if (newCardStates[i].isExpanded) {
                            setCardStates(prev => {
                                const next = prev.map(s => ({ ...s }));
                                next[i] = {
                                    ...next[i],
                                    isExpanded: true,
                                    renderMode: "open",
                                    visibleBodyHeight: newCardStates[i].visibleBodyHeight,
                                };
                                return next;
                            });
                            addTimer(() => revealBody(i + 1), STAGGER_BODY);
                        } else {
                            revealBody(i + 1); // no visual change — skip immediately
                        }
                    };
                    requestAnimationFrame(() => {
                        if (currentCalcRunId !== calcRunIdRef.current) return;
                        revealBody(0);
                    });
                };

                // Stage 2: expand headers one by one, then start stage 3.
                // Only wait STAGGER_HEADER after a card whose header actually changes —
                // skip collapsed→collapsed cards instantly.
                const startStage2 = () => {
                    setDisableHeaderTransition(false);
                    setEntryAnimStage(2);
                    const revealHeader = (i) => {
                        if (i >= N) {
                            addTimer(startStage3, HEADER_TRANSITION_MS + 80);
                            return;
                        }
                        const state = newCardStates[i];
                        const targetMode = state.renderMode === "open" ? "medium" : state.renderMode;
                        if (targetMode !== "collapsed") {
                            setCardStates(prev => {
                                const next = prev.map(s => ({ ...s }));
                                next[i] = {
                                    ...next[i],
                                    renderMode: targetMode,
                                    priorCompressedMode: state.priorCompressedMode,
                                    lastCollapsedMode: state.lastCollapsedMode,
                                };
                                return next;
                            });
                            addTimer(() => revealHeader(i + 1), STAGGER_HEADER);
                        } else {
                            revealHeader(i + 1); // no visual change — skip immediately
                        }
                    };
                    requestAnimationFrame(() => {
                        if (currentCalcRunId !== calcRunIdRef.current) return;
                        revealHeader(0);
                    });
                };

                const lastCardTime = Math.min(N - 1, 8) * STAGGER_CARD;
                addTimer(startStage2, lastCardTime + 80);
            });
        });
    };

    const toggleCard = (index) => {
        hasUserInteractedRef.current = true;
        setScrollEnabled(true);
        setDisableBodyTransition(false);
        setDisableHeaderTransition(false);
        if (revertAnimationTimerRef.current) {
            clearTimeout(revertAnimationTimerRef.current);
            revertAnimationTimerRef.current = null;
        }
        isRevertAnimatingRef.current = false;
        layoutMeasureTokenRef.current += 1;

        setCardStates((prevStates) => {
            const newStates = prevStates.map(s => ({ ...s }));
            const currentMode = newStates[index]?.renderMode;

            if (currentMode === "open") {
                // Open ticket header clicked → collapse to orphan header only
                newStates[index].isExpanded = false;
                newStates[index].isHalfOpen = false;
                newStates[index].visibleBodyHeight = 0;
                newStates[index].renderMode = "medium";
                newStates[index].priorCompressedMode = "medium";
            } else if (currentMode === "medium") {
                // Orphan header clicked → open this ticket only.
                // Match the height of any already-open card so all open cards are the same size.
                const alreadyOpen = newStates.find((s, i) => i !== index && s.isExpanded);
                const consistentHeight = alreadyOpen
                    ? alreadyOpen.visibleBodyHeight
                    : (newStates[index].fullHeight || defaultHeightOfOpenCard);
                newStates[index].isExpanded = true;
                newStates[index].isHalfOpen = false;
                newStates[index].visibleBodyHeight = consistentHeight;
                newStates[index].renderMode = "open";
            } else {
                // Stack (collapsed) clicked → convert clicked + before + after to orphan headers
                const toConvert = new Set([index]);
                if (index > 0) toConvert.add(index - 1);
                if (index < newStates.length - 1) toConvert.add(index + 1);

                toConvert.forEach((i) => {
                    newStates[i].isExpanded = false;
                    newStates[i].isHalfOpen = false;
                    newStates[i].visibleBodyHeight = 0;
                    newStates[i].renderMode = "medium";
                    newStates[i].priorCompressedMode = "medium";
                });
            }

            return newStates;
        });

        if (autoRevertTimerRef.current) {
            clearTimeout(autoRevertTimerRef.current);
        }

        if (autoRevertDurationinSec > 0) {
            const currentVersion = dataVersionRef.current;
            autoRevertTimerRef.current = setTimeout(() => {
                if (initialStateRef.current &&
                    initialStateRef.current.dataVersion === currentVersion &&
                    initialStateRef.current.states.length === cardStates.length) {
                    const restoredState = JSON.parse(JSON.stringify(initialStateRef.current.states));
                    // Reset interaction flags so measureAndApply can re-run after the
                    // revert animation settles. Allow transitions so the reset animates smoothly.
                    hasUserInteractedRef.current = false;
                    prevExpandedRef.current = new Set();
                    setScrollEnabled(false);
                    isRevertAnimatingRef.current = true;
                    setDisableBodyTransition(false);
                    setDisableHeaderTransition(false);
                    layoutMeasureTokenRef.current += 1;
                    setCardStates(restoredState);
                    revertAnimationTimerRef.current = setTimeout(() => {
                        isRevertAnimatingRef.current = false;
                        revertAnimationTimerRef.current = null;
                        setDisableBodyTransition(true);
                    }, 1100);
                }
                autoRevertTimerRef.current = null;
            }, autoRevertDurationinSec * 1000);
        }
    };

    const formatOwnerName = (name) => {
        if (!name) return "";
        const words = name.trim().split(/\s+/);
        if (words.length === 1) return words[0];
        const formatted = [words[0]];
        for (let i = 1; i < words.length; i++) {
            formatted.push(words[i][0].toUpperCase());
        }
        return formatted.join(" ");
    };

    const getInitials = (name) => {
        if (!name) return "?";
        const words = name.trim().split(/\s+/);
        if (words.length === 1) return words[0][0]?.toUpperCase() || "?";
        return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    };

    const handleImageError = (cardId) => {
        setImageErrors((prev) => ({ ...prev, [cardId]: true }));
    };

    const shouldShowCards = isReady || hasInitiallyLoadedRef.current;

    return (
        <div ref={parentRef} style={{ height: "calc(100% - 0.05em)", width: "100%" }}>
            <div
                className={isDarkTheme ? "slim-scroll-dark" : "slim-scroll"}
                ref={scrollContainerRef}
                style={{
                    width: "100%",
                    height: "100%",
                    overflowY: scrollEnabled ? "auto" : "hidden",
                    overflowX: "hidden",
                    opacity: shouldShowCards ? 1 : 0,
                    visibility: shouldShowCards ? "visible" : "hidden",
                    transition: "none",
                    backgroundColor: isDarkTheme ? "#1a1a1a" : "#f0f0f0",
                }}
            >
                <div ref={gridRef} className="main-card-body" style={{
                    margin: "8px", overflow: "visible",
                }}>
                    {displayCardsData.map((card, index) => (
                        (() => {
                            const cardState = cardStates[index] || {};
                            const isExpanded = Boolean(cardState.isExpanded);
                            const renderMode = (cardState.renderMode === "open" || isExpanded)
                                ? "open"
                                : (cardState.renderMode === "medium" ? "medium" : "collapsed");
                            // Stage 1: all cards show as collapsed (slim header strips during slide-in).
                            // Stages 2-3 and idle: show actual renderMode.
                            const isCollapsedMode = renderMode === "collapsed";
                            const isMediumMode = renderMode === "medium";
                            const isBodyOpen = renderMode === "open";
                            const cardStaggerDelay = Math.min(index, 8) * 55;
                            // Body fades in via CSS keyframe (delay baked in) — no React state
                            // change needed mid-animation, so no reflow stutter.
                            const bodyAnimation = "none";
                            const fullHeight = Number(cardState.fullHeight || 0);
                            const visibleBodyHeight = Number(cardState.visibleBodyHeight || 0);
                            const bodyHeight = renderMode === "open"
                                ? `${Math.max(0, Math.max(1, visibleBodyHeight || fullHeight)).toFixed(3)}px`
                                : "0px";
                            const bodyPadding = renderMode === "open" ? `${compactStatusStyle.bodyPaddingPx}px` : "0";
                            const contentOpacity = isBodyOpen ? 1 : 0;
                            const contentTransitionDisabled = ((entryAnimStage === 1 || entryAnimStage === 2) && !hasUserInteractedRef.current) || disableBodyTransition;
                            const contentTransition = (delayMs) => contentTransitionDisabled ? "none" : `opacity 680ms cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms`;
                            const normalHeaderPadPx = 2;
                            const normalHeaderMinHeightPx = 22;
                            const normalHeaderFontRem = 0.9;

                            const mediumHeaderPadPx = normalHeaderPadPx;
                            const mediumHeaderMinHeightPx = normalHeaderMinHeightPx;
                            const mediumHeaderFontRem = normalHeaderFontRem;

                            const collapsedHeaderPadPx = 0;
                            const collapsedHeaderMinHeightPx = 3;
                            const collapsedHeaderFontRem = Math.max(0.44, Number((mediumHeaderFontRem - 0.18).toFixed(2)));

                            const headerPadPx = isCollapsedMode
                                ? collapsedHeaderPadPx
                                : isMediumMode
                                    ? mediumHeaderPadPx
                                    : normalHeaderPadPx;
                            const headerMinHeightPx = isCollapsedMode
                                ? collapsedHeaderMinHeightPx
                                : isMediumMode
                                    ? mediumHeaderMinHeightPx
                                    : normalHeaderMinHeightPx;
                            const headerFontSize = isCollapsedMode
                                ? `${collapsedHeaderFontRem}rem`
                                : isMediumMode
                                    ? `${mediumHeaderFontRem}rem`
                                    : `${normalHeaderFontRem}rem`;
                            const headerLeftPad = isCollapsedMode ? "1px" : "8px";
                            const headerLineHeight = isCollapsedMode ? 1 : isMediumMode ? 1.12 : 1.2;
                            const headerDisplayText = isCollapsedMode ? "" : `${card.headerText || ""}`;
                            const isLastCard = index === displayCardsData.length - 1;
                            const headerColor = getHeaderColor(card.percent);
                            const cardGapPx = isDarkTheme ? "5px" : "3px";
                            const marginBottomPx = isCollapsedMode
                                ? (cardState.lastCollapsedMode
                                    ? (isOpenTicketPlacementTop
                                        ? (cardState.toggledEnabled ? cardGapPx : "0px")
                                        : cardGapPx)
                                    : "0px")
                                : (isLastCard ? "0px" : cardGapPx);

                            return (
                                <div
                                    className="card"
                                    key={card.id}
                                    ref={(el) => (cardRefs.current[index] = el)}
                                    style={{
                                        backgroundColor: themeStyles.cardBackground,
                                        border: isCollapsedMode ? "none" : themeStyles.cardBorder,
                                        boxShadow: isCollapsedMode ? "none" : themeStyles.cardBoxShadow,
                                        color: themeStyles.bodyTextColor,
                                        borderRadius: (isMediumMode || isCollapsedMode) ? "0px" : "5px",
                                        opacity: isReady ? undefined : 0,
                                        animation: (entryAnimStage === 1 && isReady && !hasUserInteractedRef.current)
                                            ? `widgetCardAppear 1ms step-end ${cardStaggerDelay}ms both`
                                            : "none",
                                        marginBottom: marginBottomPx,
                                        overflow: "hidden",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                    onMouseEnter={(event) => {
                                        if (!isReady || isEntryAnimatingRef.current) return;
                                        event.currentTarget.style.animation = "none";
                                        event.currentTarget.style.transition = "transform 420ms cubic-bezier(0.34, 1.56, 0.64, 1)";
                                        event.currentTarget.style.transform = "translateX(3px)";
                                    }}
                                    onMouseLeave={(event) => {
                                        if (!isReady || isEntryAnimatingRef.current) return;
                                        event.currentTarget.style.transition = "transform 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)";
                                        event.currentTarget.style.transform = "translateX(0px)";
                                    }}
                                >
                                    <div
                                        className="card-header fw-bold"
                                        style={{
                                            cursor: "pointer",
                                            overflow: "hidden",
                                            paddingTop: `${headerPadPx}px`,
                                            paddingBottom: `${headerPadPx}px`,
                                            minHeight: `${headerMinHeightPx}px`,
                                            fontSize: headerFontSize,
                                            lineHeight: headerLineHeight,
                                            backgroundColor: headerColor,
                                            border: isCollapsedMode ? `1px solid ${getStackHeaderBorderColor(headerColor)}` : `1px solid ${headerColor}`,
                                            color: compactStatusStyle.showHeaderText ? "#ffffff" : "transparent",
                                            WebkitFontSmoothing: "antialiased",
                                            MozOsxFontSmoothing: "grayscale",
                                            textRendering: "optimizeLegibility",
                                            letterSpacing: "0.02em",
                                            borderTopLeftRadius: isCollapsedMode || isMediumMode ? "0" : "4px",
                                            borderTopRightRadius: isCollapsedMode || isMediumMode ? "0" : "4px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            paddingLeft: headerLeftPad,
                                            paddingRight: headerLeftPad,
                                            transform: "translateZ(0)",
                                            backfaceVisibility: "hidden",
                                            WebkitBackfaceVisibility: "hidden",
                                            transition: disableHeaderTransition
                                                ? "color 250ms ease"
                                                : (() => {
                                                    const hMs = (entryAnimStage === 2 && !hasUserInteractedRef.current) ? 220 : 420;
                                                    return `color 250ms ease, min-height ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), font-size ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), padding ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), line-height ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), border-radius ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
                                                })(),
                                        }}
                                        title={headerDisplayText}
                                        onClick={() => toggleCard(index)}
                                    >
                                        <span
                                            ref={el => { headerSpanRefs.current[card.id] = el; }}
                                            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: "1 1 0", minWidth: 0 }}
                                        >
                                            {headerDisplayText}
                                        </span>
                                        {!isCollapsedMode && card.percent && (
                                            <span style={{ flexShrink: 0, marginLeft: "6px", whiteSpace: "nowrap" }}>
                                                {(headerLabelModesRef.current[card.id] || 'full') === 'full'
                                                    ? `${card.percent} of Target`
                                                    : card.percent}
                                            </span>
                                        )}
                                    </div>

                                    <div
                                        className="card-body collapse show"
                                        style={{
                                            backgroundColor: themeStyles.cardBackground,
                                            color: themeStyles.bodyTextColor,
                                            overflow: "hidden",
                                            animation: bodyAnimation,
                                            transition: disableBodyTransition ? "none" : "flex-basis 900ms cubic-bezier(0.34, 1.08, 0.64, 1), opacity 700ms cubic-bezier(0.22, 1, 0.36, 1)",
                                            flexBasis: bodyHeight,
                                            flexGrow: 0,
                                            flexShrink: 0,
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "flex-start",
                                            gap: isBodyOpen ? "4px" : "0",
                                            padding: bodyPadding,
                                            margin: "0",
                                            opacity: isBodyOpen ? 1 : 0,
                                            pointerEvents: (isBodyOpen && entryAnimStage === 0) ? "auto" : "none",
                                            borderRadius: "0",
                                            boxSizing: "border-box",
                                            contain: "layout paint",
                                            transform: "translateZ(0)",
                                            backfaceVisibility: "hidden",
                                            willChange: !disableBodyTransition ? "flex-basis, opacity" : "auto",
                                        }}
                                    >
                                        <div
                                            className="row align-items-start"
                                            style={{
                                                flex: "0 0 auto",
                                                opacity: contentOpacity,
                                                transition: contentTransition(200),
                                            }}
                                        >
                                            <div className="col text-start">
                                                <div style={{ fontSize: `${compactStatusStyle.topLeftFontRem}rem`, fontWeight: "bold" }}>
                                                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        Target hours: {card.targetHours != null && !isNaN(Number(card.targetHours)) ? +Number(card.targetHours).toFixed(2) : "NA"}
                                                    </div>
                                                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        Actual hours: {card.actualHours != null && !isNaN(Number(card.actualHours)) ? +Number(card.actualHours).toFixed(2) : "NA"}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col d-flex flex-column align-items-end">
                                                <div style={{ width: `${compactStatusStyle.avatarWrapWidthPx}px`, display: "flex", justifyContent: "center" }}>
                                                    {!card.imageUrl || imageErrors[card.id] ? (
                                                        <svg
                                                            width={compactStatusStyle.avatarSizePx}
                                                            height={compactStatusStyle.avatarSizePx}
                                                            viewBox="0 0 200 200"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            style={{ borderRadius: "50%" }}
                                                        >
                                                            <circle cx="100" cy="100" r="78" fill="#EBCDB2" stroke="#E3B187" strokeWidth="1.5" />
                                                            <circle cx="100" cy="68" r="26" fill="#B8B8B8" />
                                                            <path d="M72 100 L128 100 L100 178 Z" fill="#B8B8B8" />
                                                        </svg>
                                                    ) : (
                                                        <img
                                                            src={card.imageUrl}
                                                            alt="avatar"
                                                            style={{
                                                                width: compactStatusStyle.avatarSizePx,
                                                                height: compactStatusStyle.avatarSizePx,
                                                                borderRadius: "50%",
                                                            }}
                                                            onError={() => handleImageError(card.id)}
                                                        />
                                                    )}
                                                </div>

                                                <div
                                                    style={{
                                                        width: `${compactStatusStyle.avatarWrapWidthPx}px`,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        textAlign: "center",
                                                        color: themeStyles.bodyTextColor,
                                                        fontSize: `${compactStatusStyle.ownerFontRem}rem`,
                                                    }}
                                                    title={card.owner}
                                                >
                                                    {formatOwnerName(card.owner)}
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            style={{
                                                flex: "1 1 auto",
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                                paddingTop: "2px",
                                                paddingBottom: "2px",
                                                minHeight: "52px",
                                                overflow: "hidden",
                                                minWidth: 0,
                                            }}
                                        >
                                            <p
                                                className="mt-0 fw-bold text-center"
                                                style={{
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: compactStatusStyle.titleLines,
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    fontSize: `${compactStatusStyle.titleFontRem}rem`,
                                                    lineHeight: 1.2,
                                                    marginTop: "0",
                                                    marginBottom: "0.15rem",
                                                    opacity: contentOpacity,
                                                    transition: contentTransition(360),
                                                }}
                                                title={card.title}
                                            >
                                                {card.title}
                                            </p>

                                            <p
                                                className="mt-1 text-center"
                                                style={{
                                                    color: themeStyles.mutedTextColor,
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: compactStatusStyle.descLines,
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    fontSize: `${compactStatusStyle.descFontRem}rem`,
                                                    lineHeight: 1.2,
                                                    marginTop: "0",
                                                    marginBottom: "0.4rem",
                                                    opacity: contentOpacity,
                                                    transition: contentTransition(520),
                                                }}
                                                title={card.description}
                                            >
                                                {card.description}
                                            </p>
                                            {card.description1 && (
                                                <p
                                                    className="text-center"
                                                    style={{
                                                        color: themeStyles.mutedTextColor,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        fontSize: `${compactStatusStyle.descFontRem}rem`,
                                                        lineHeight: 1.2,
                                                        marginTop: "0",
                                                        marginBottom: "0.15rem",
                                                        opacity: contentOpacity,
                                                        transition: contentTransition(440),
                                                    }}
                                                    title={card.description1}
                                                >
                                                    {card.description1}
                                                </p>
                                            )}
                                        </div>

                                        <div
                                            className="row small"
                                            style={{
                                                flex: "0 0 auto",
                                                alignItems: "center",
                                                fontSize: `${compactStatusStyle.metaFontRem}rem`,
                                                lineHeight: 1.2,
                                                opacity: contentOpacity,
                                                transition: contentTransition(680),
                                            }}
                                        >
                                            <div className="col">
                                                <b>Opened</b>
                                                <br />
                                                {card.openDate ?? "NA"}
                                            </div>
                                            <div className="col text-center">
                                                <b>Promised</b>
                                                <br />
                                                {card.promiseDate ?? "NA"}
                                            </div>
                                            <div className="col text-end">
                                                <b>Completed</b>
                                                <br />
                                                {card.completedDate ?? card.status ?? "NA"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()
                    ))}
                </div>
            </div>
        </div>
    );
}
